import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, AlertCircle, Monitor } from 'lucide-react'
import { getOpenPlaceTokenPlate, runOpenPlaceTokenView, triggerOpenPlaceTokenJourney, getOpenPlaceTokenJourney, getOpenPlaceTokenInterviewer, submitOpenPlaceTokenInterviewer } from '../api/openplace-token'
import { PlateLayout } from '../components/PlateLayout'
import { DEFAULT_JOURNEY_API, type JourneyApi } from '../components/PlateViewRenderer'
import { EnergyOSHomeDashboard } from './EnergyOSHomeDashboard'
import type { BPlateInputParam } from '../types'

// Bespoke visual redesign lives entirely in EnergyOSHomeDashboard.tsx, branched to by plate name
// so every other business's OpenPlace page keeps using the fully generic PlateLayout rendering
// below — this page itself stays domain-agnostic.
const EOS_DASHBOARD_PLATE_NAME = 'EnergyOS — Home Dashboard'

// The genuinely public per-home dashboard link (e.g. "Copy homeowner link" on a Home record) —
// unauthenticated, scoped by the opaque token in the URL, safe to share with someone who has no
// Rabbit account at all. Distinct from /open/:id, which is the authenticated back-office preview
// of a plate business-wide and has no way to resolve which home to scope a Drinker-fed view to.
export function OpenPlaceTokenView() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()

  const { data: plate, isLoading, isError } = useQuery({
    queryKey: ['openplace-token-plate', token],
    queryFn: () => getOpenPlaceTokenPlate(token!).then(r => r.data),
    enabled: !!token,
    retry: false,
  })

  const [runInputs, setRunInputs] = useState<Record<string, string> | null>(null)

  const handleParamUpdate = (updates: Record<string, string>) => {
    setRunInputs(prev => {
      if (!prev) return prev
      const merged = { ...prev, ...updates }
      return JSON.stringify(merged) === JSON.stringify(prev) ? prev : merged
    })
  }

  // scopeCoffeeId itself is always resolved server-side from the token, regardless of what's
  // sent here — this only needs to resolve any *other* input params the plate happens to have.
  const resolveInputs = (params: BPlateInputParam[]): Record<string, string> => {
    const result: Record<string, string> = {}
    for (const p of params) {
      const val = p.fixedValue ?? searchParams.get(p.name) ?? ''
      if (val) result[p.name] = val
    }
    return result
  }

  useEffect(() => {
    setRunInputs(null)
  }, [token])

  useEffect(() => {
    if (!plate || runInputs !== null) return
    setRunInputs(resolveInputs(plate.inputParams))
  }, [plate, token]) // eslint-disable-line react-hooks/exhaustive-deps

  // Every journey call goes through the token routes, forms included (e.g. a client refusing a
  // quote with a comment) — the server scopes each one to this link's own record.
  const journeyApi: JourneyApi = {
    ...DEFAULT_JOURNEY_API,
    trigger: (preparationId, initialPayload) => triggerOpenPlaceTokenJourney(token!, preparationId, initialPayload),
    get: (journeyId) => getOpenPlaceTokenJourney(token!, journeyId),
    getInterviewer: (journeyId) => getOpenPlaceTokenInterviewer(token!, journeyId),
    submitInterviewer: (journeyId, d) => submitOpenPlaceTokenInterviewer(token!, journeyId, d),
  }

  if (plate && runInputs && plate.name === EOS_DASHBOARD_PLATE_NAME) {
    return (
      <EnergyOSHomeDashboard
        plate={plate}
        runInputs={runInputs}
        runView={(viewId, inputs) => runOpenPlaceTokenView(token!, viewId, inputs).then(r => r.data)}
        journeyApi={journeyApi}
      />
    )
  }

  return (
    <div className="min-h-screen bg-paper">
      <header className="h-10 bg-white border-b border-paper-border flex items-center px-4">
        <div className="flex items-center gap-2">
          <div className="w-[18px] h-[18px] rounded bg-accent flex items-center justify-center shrink-0">
            <span className="text-white text-[8px] font-bold leading-none">R</span>
          </div>
          <span className="font-semibold text-sm text-ink tracking-tight leading-none">
            {plate?.name ?? 'OpenPlace'}
          </span>
        </div>
      </header>

      <div className="px-2 py-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={20} className="animate-spin text-ink-dim" />
          </div>
        ) : isError || !plate ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex items-center gap-2 text-sm text-red-500">
              <AlertCircle size={16} /> This link is invalid or no longer active
            </div>
          </div>
        ) : runInputs ? (
          <div className={plate.width ? `${plate.width} mx-auto` : undefined}>
            <PlateLayout
              plate={plate}
              runInputs={runInputs}
              onParamUpdate={handleParamUpdate}
              runView={(viewId, inputs) => runOpenPlaceTokenView(token!, viewId, inputs).then(r => r.data)}
              journeyApi={journeyApi}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={20} className="animate-spin text-ink-dim" />
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-1.5 text-theme-xs text-ink-dim py-4">
        <Monitor size={11} /> OpenPlace
      </div>
    </div>
  )
}
